import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { api } from '../utils/api'

// The eBay requests view: what buyers have open on eBay, with eBay's deadline.
// Loaded ONCE for the whole app and shared, so the menu warning, the Cases tab
// and the case notice can never disagree. Spec: docs/specs_20261013_ebay_return_flow.md

// A deadline moves from amber to red while a tab sits open, so the view is
// reloaded on the same beat as the worker's sync.
export const EBAY_REQUESTS_REFRESH_MS = 5 * 60 * 1000

const EMPTY_VIEW = {
  requests: [],
  urgent_count: 0,
  attention_count: 0,
  sync: { last_ok_at: null, last_error: null, stale: false }
}
const NOT_PROVIDED = { view: EMPTY_VIEW, checking: false, checkNow: async () => {}, forCase: () => [] }

const EbayRequestsContext = createContext(NOT_PROVIDED)

export function EbayRequestsProvider({ enabled, children }) {
  const [view, setView] = useState(EMPTY_VIEW)
  const [checking, setChecking] = useState(false)

  // A load failure shows as a stale list, never as an empty healthy one.
  const load = useCallback(async (request) => {
    try {
      const { requests = [], urgent_count = 0, attention_count = 0, sync } = await request()
      setView({ requests, urgent_count, attention_count, sync: sync || EMPTY_VIEW.sync })
    } catch (error) {
      console.error('Failed to load eBay requests:', error)
      setView(current => ({
        ...current,
        sync: { ...current.sync, stale: true, last_error: 'Prism could not load the eBay requests.' }
      }))
    }
  }, [])

  useEffect(() => {
    if (!enabled) return undefined
    const refresh = () => load(() => api.get('/api/orders/ebay-requests'))
    refresh()
    const interval = setInterval(refresh, EBAY_REQUESTS_REFRESH_MS)
    return () => clearInterval(interval)
  }, [enabled, load])

  const checkNow = useCallback(async () => {
    setChecking(true)
    await load(() => api.post('/api/orders/ebay-requests/sync'))
    setChecking(false)
  }, [load])

  const value = useMemo(() => ({
    view,
    checking,
    checkNow,
    forCase: (returnRequestId) => view.requests
      .filter(request => request.return_request_id === returnRequestId)
      .sort((a, b) => Number(!!a.closed_at) - Number(!!b.closed_at))
  }), [view, checking, checkNow])

  return <EbayRequestsContext.Provider value={value}>{children}</EbayRequestsContext.Provider>
}

export function useEbayRequests() {
  return useContext(EbayRequestsContext)
}
